import { useEffect, useMemo, useState } from 'react';
import Typography from '@components/common/typography';
import styles from './styles.module.scss';
import { motion } from 'framer-motion';
import { pageAnimation } from '@const/animation';
import Button from '@components/common/button';
import { routes } from '@const/routes';
import { useNavigationContext } from '@hooks/context/useNavigationContext';
import { useUserContext } from '@hooks/context/useUserContext';
import { prettifyAmount } from '@utils/index';
import { useLocales } from '@hooks/helpers/useLocales';
import { TAccountLimits } from '@xeppt/xeppt-sdk/types';
import Tabs from '@components/tabs';
import { Icon } from '@components/icons';
import { ELoyaltyLevel } from '@xeppt/xeppt-sdk/types/loyalty';
import { EFundsOperation } from '@enum';

const types: {
    label: string;
    value: string;
    sendKey: Exclude<EFundsOperation, EFundsOperation.CARD_DEBIT_DEPOSIT>;
    requestKey?: Exclude<EFundsOperation, EFundsOperation.CARD_DEBIT_DEPOSIT>;
    sendLimits?: TAccountLimits | undefined;
    requestLimits?: TAccountLimits | undefined;
}[] = [
    {
        label: 'Wallet',
        value: 'wallet',
        sendKey: EFundsOperation.WALLET_CREDIT_TRANSFER,
        requestKey: EFundsOperation.WALLET_DEBIT_REQUEST,
        sendLimits: undefined,
        requestLimits: undefined
    },
    {
        label: 'Interac®',
        value: 'interac',
        sendKey: EFundsOperation.E_TRANSFER_CREDIT_TRANSFER,
        requestKey: EFundsOperation.E_TRANSFER_DEBIT_REQUEST,
        sendLimits: undefined,
        requestLimits: undefined
    },
    // { label: 'Linked card', value: 'eps', request: EFundsOperation.EPS_DEBIT_DEPOSIT },
    {
        label: 'Bank account',
        value: 'eft',
        sendKey: EFundsOperation.EFT_CREDIT_WITHDRAWAL,
        requestKey: EFundsOperation.EFT_DEBIT_DEPOSIT,
        sendLimits: undefined,
        requestLimits: undefined
    },
    {
        label: 'Load card',
        value: 'load_card',
        sendKey: EFundsOperation.WALLET_CREDIT_CARD_DEPOSIT,
        sendLimits: undefined
    }
];

const levels = [
    { label: 'Silver', value: ELoyaltyLevel.A },
    { label: 'Gold', value: ELoyaltyLevel.B },
    { label: 'Platinum', value: ELoyaltyLevel.C }
];

const SettingsLimitsLayout = () => {
    const { handleBack } = useNavigationContext();
    const { limits, limitsSettings, loyalty } = useUserContext();
    const { submitLocale } = useLocales();
    const [activeType, setActiveType] = useState<(typeof types)[0]>(types[0]);
    const [activeLevel, setActiveLevel] = useState<ELoyaltyLevel>(ELoyaltyLevel.A);

    const isCurrentLevel = useMemo(() => {
        const foundLevel = levels.find((item) => item.value === activeLevel)?.value;
        return foundLevel === loyalty?.level;
    }, [loyalty, limits, activeLevel]);

    const currentLimits = useMemo(() => {
        let result = types;
        result = result.map((item) => {
            const iterrationResult: { [key: string]: TAccountLimits | undefined } = {
                sendLimits: undefined,
                requestLimits: undefined
            };
            const sendLimits: TAccountLimits | undefined = isCurrentLevel
                ? (limits?.[item.sendKey as EFundsOperation] as TAccountLimits)
                : (limitsSettings?.[activeLevel]?.[item.sendKey] as TAccountLimits);
            const requestLimits: TAccountLimits | undefined = isCurrentLevel
                ? item.requestKey
                    ? limits?.[item.requestKey]
                    : undefined
                : item.requestKey
                  ? (limitsSettings?.[activeLevel]?.[item.requestKey] as TAccountLimits)
                  : undefined;
            if (
                sendLimits?.per_transfer !== 0 ||
                sendLimits?.amount_transfer_min !== 0 ||
                sendLimits?.daily !== 0 ||
                sendLimits?.weekly !== 0 ||
                sendLimits?.monthly !== 0
            ) {
                iterrationResult.sendLimits = sendLimits;
            }
            if (
                requestLimits?.per_transfer !== 0 ||
                requestLimits?.daily !== 0 ||
                requestLimits?.amount_transfer_min !== 0 ||
                requestLimits?.weekly !== 0 ||
                requestLimits?.monthly !== 0
            ) {
                iterrationResult.requestLimits = requestLimits;
            }
            return {
                ...item,
                ...iterrationResult
            };
        });

        return result;
    }, [limits, isCurrentLevel, limitsSettings, activeLevel]);

    const activeTabLimits = useMemo(() => {
        return currentLimits.find((item) => item.value === activeType.value);
    }, [currentLimits, activeType]);

    useEffect(() => {
        if (loyalty) {
            const foundLevel = levels.find((item) => item.value === loyalty.level)?.value;
            setActiveLevel(foundLevel || levels[0].value);
        }
    }, [levels, loyalty]);

    const operationTabs = useMemo(() => {
        return currentLimits.filter((item) => item.sendLimits || item.requestLimits);
    }, [activeLevel, currentLimits]);

    useEffect(() => {
        if (operationTabs.length) {
            setActiveType(operationTabs[0]);
        }
    }, [operationTabs]);

    return (
        <motion.div {...pageAnimation} className={styles.wrapper}>
            <div className={styles.header}>
                <Button
                    className={styles.back}
                    leftIcon="arrow_left"
                    variant="text-gray"
                    onClick={() => handleBack(routes.settings)}>
                    {submitLocale('back')}
                </Button>
                <Typography variant="h4">Limits</Typography>
            </div>
            <Tabs
                onChange={(val) => setActiveLevel(val)}
                tabs={levels.map(({ label, value }) => {
                    if (value === loyalty?.level) {
                        return {
                            label,
                            value,
                            after: (
                                <div className={styles.active}>
                                    <Icon name="check" />
                                </div>
                            )
                        };
                    }
                    return { label, value };
                })}
                activeTab={activeLevel}
            />
            <div className={styles.tabs}>
                {operationTabs.map((item) => {
                    return (
                        <Button
                            variant={
                                activeType.value === item.value ? 'primary' : 'outlined-primary'
                            }
                            className={activeType.value === item.value ? styles.isActive : ''}
                            key={item.value}
                            onClick={() => setActiveType(item)}>
                            {item.label}
                        </Button>
                    );
                })}
            </div>
            <div className={styles.content}>
                {activeTabLimits?.sendLimits && (
                    <div className={styles.block}>
                        <Typography variant="body2" weight="semibold">
                            Sending limits
                        </Typography>
                        <div className={styles.divider} />
                        <div className={styles.content_wrapper}>
                            {activeTabLimits?.sendLimits.daily > 0 && (
                                <div className={styles.limit_item}>
                                    <div className={styles.row}>
                                        <Typography variant="body3">Daily transactions</Typography>
                                        <Typography variant="body3" weight="semibold">
                                            {prettifyAmount(
                                                activeTabLimits?.sendLimits.daily.toFixed(2)
                                            )}{' '}
                                            CAD
                                        </Typography>
                                    </div>
                                    {isCurrentLevel && (
                                        <>
                                            <div className={styles.progress_wrapper}>
                                                <div
                                                    className={styles.progress}
                                                    style={{
                                                        width: `${activeTabLimits?.sendLimits?.daily_used / (activeTabLimits?.sendLimits.daily / 100)}%`
                                                    }}
                                                />
                                            </div>
                                            <div className={styles.row}>
                                                <Typography variant="body4" weight="semibold">
                                                    {prettifyAmount(
                                                        activeTabLimits?.sendLimits.daily_used.toFixed(
                                                            2
                                                        )
                                                    )}{' '}
                                                    CAD <span>used</span>
                                                </Typography>
                                                <Typography variant="body4" weight="semibold">
                                                    {prettifyAmount(
                                                        (
                                                            (activeTabLimits?.sendLimits.daily ||
                                                                0) -
                                                            (activeTabLimits?.sendLimits
                                                                .daily_used || 0)
                                                        ).toFixed(2)
                                                    )}{' '}
                                                    CAD <span>available</span>
                                                </Typography>
                                            </div>
                                        </>
                                    )}
                                </div>
                            )}
                            {activeTabLimits?.sendLimits.weekly > 0 && (
                                <div className={styles.limit_item}>
                                    <div className={styles.row}>
                                        <Typography variant="body3">Weekly transactions</Typography>
                                        <Typography variant="body3" weight="semibold">
                                            {prettifyAmount(
                                                activeTabLimits?.sendLimits.weekly.toFixed(2)
                                            )}{' '}
                                            CAD
                                        </Typography>
                                    </div>
                                    {isCurrentLevel && (
                                        <>
                                            <div className={styles.progress_wrapper}>
                                                <div
                                                    className={styles.progress}
                                                    style={{
                                                        width: `${activeTabLimits?.sendLimits.weekly_used / (activeTabLimits?.sendLimits.weekly / 100)}%`
                                                    }}
                                                />
                                            </div>
                                            <div className={styles.row}>
                                                <Typography variant="body4" weight="semibold">
                                                    {prettifyAmount(
                                                        activeTabLimits?.sendLimits.weekly_used.toFixed(
                                                            2
                                                        )
                                                    )}{' '}
                                                    CAD <span>used</span>
                                                </Typography>
                                                <Typography variant="body4" weight="semibold">
                                                    {prettifyAmount(
                                                        (
                                                            (activeTabLimits?.sendLimits.weekly ||
                                                                0) -
                                                            (activeTabLimits?.sendLimits
                                                                .weekly_used || 0)
                                                        ).toFixed(2)
                                                    )}{' '}
                                                    CAD <span>available</span>
                                                </Typography>
                                            </div>
                                        </>
                                    )}
                                </div>
                            )}
                            {activeTabLimits?.sendLimits.monthly > 0 && (
                                <div className={styles.limit_item}>
                                    <div className={styles.row}>
                                        <Typography variant="body3">
                                            Monthly transactions
                                        </Typography>
                                        <Typography variant="body3" weight="semibold">
                                            {prettifyAmount(
                                                activeTabLimits?.sendLimits.monthly.toFixed(2)
                                            )}{' '}
                                            CAD
                                        </Typography>
                                    </div>
                                    {isCurrentLevel && (
                                        <>
                                            <div className={styles.progress_wrapper}>
                                                <div
                                                    className={styles.progress}
                                                    style={{
                                                        width: `${activeTabLimits?.sendLimits.monthly_used / (activeTabLimits?.sendLimits.monthly / 100)}%`
                                                    }}
                                                />
                                            </div>
                                            <div className={styles.row}>
                                                <Typography variant="body4" weight="semibold">
                                                    {prettifyAmount(
                                                        activeTabLimits?.sendLimits.monthly_used.toFixed(
                                                            2
                                                        )
                                                    )}{' '}
                                                    CAD <span>used</span>
                                                </Typography>
                                                <Typography variant="body4" weight="semibold">
                                                    {prettifyAmount(
                                                        (
                                                            (activeTabLimits?.sendLimits.monthly ||
                                                                0) -
                                                            (activeTabLimits?.sendLimits
                                                                .monthly_used || 0)
                                                        ).toFixed(2)
                                                    )}{' '}
                                                    CAD <span>available</span>
                                                </Typography>
                                            </div>
                                        </>
                                    )}
                                </div>
                            )}
                            <div className={styles.transaction_styles}>
                                {activeTabLimits?.sendLimits.per_transfer > 0 && (
                                    <div className={styles.row}>
                                        <Typography variant="body3">
                                            Maximum amount per transaction
                                        </Typography>
                                        <Typography variant="body3" weight="semibold">
                                            {prettifyAmount(
                                                activeTabLimits?.sendLimits.per_transfer.toFixed(2)
                                            )}{' '}
                                            CAD
                                        </Typography>
                                    </div>
                                )}

                                {activeTabLimits?.sendLimits.amount_transfer_min > 0 && (
                                    <div className={styles.row}>
                                        <Typography variant="body3">
                                            Minimum amount per transaction
                                        </Typography>
                                        <Typography variant="body3" weight="semibold">
                                            {prettifyAmount(
                                                activeTabLimits?.sendLimits.amount_transfer_min.toFixed(
                                                    2
                                                )
                                            )}{' '}
                                            CAD
                                        </Typography>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                )}
                {activeTabLimits?.requestLimits && (
                    <div className={styles.block}>
                        <Typography variant="body2" weight="semibold">
                            Requesting limits
                        </Typography>
                        <div className={styles.divider} />
                        <div className={styles.content_wrapper}>
                            {activeTabLimits?.requestLimits.daily > 0 && (
                                <div className={styles.limit_item}>
                                    <div className={styles.row}>
                                        <Typography variant="body3">Daily requests</Typography>
                                        <Typography variant="body3" weight="semibold">
                                            {prettifyAmount(
                                                activeTabLimits?.requestLimits.daily.toFixed(2)
                                            )}{' '}
                                            CAD
                                        </Typography>
                                    </div>
                                    {isCurrentLevel && (
                                        <>
                                            <div className={styles.progress_wrapper}>
                                                <div
                                                    className={styles.progress}
                                                    style={{
                                                        width: `${activeTabLimits?.requestLimits.daily_used / (activeTabLimits?.requestLimits.daily / 100)}%`
                                                    }}
                                                />
                                            </div>
                                            <div className={styles.row}>
                                                <Typography variant="body4" weight="semibold">
                                                    {prettifyAmount(
                                                        activeTabLimits?.requestLimits.daily_used.toFixed(
                                                            2
                                                        )
                                                    )}{' '}
                                                    CAD <span>used</span>
                                                </Typography>
                                                <Typography variant="body4" weight="semibold">
                                                    {prettifyAmount(
                                                        (
                                                            (activeTabLimits?.requestLimits.daily ||
                                                                0) -
                                                            (activeTabLimits?.requestLimits
                                                                .daily_used || 0)
                                                        ).toFixed(2)
                                                    )}{' '}
                                                    CAD <span>available</span>
                                                </Typography>
                                            </div>
                                        </>
                                    )}
                                </div>
                            )}
                            {activeTabLimits?.requestLimits.weekly > 0 && (
                                <div className={styles.limit_item}>
                                    <div className={styles.row}>
                                        <Typography variant="body3">Weekly requests</Typography>
                                        <Typography variant="body3" weight="semibold">
                                            {prettifyAmount(
                                                activeTabLimits?.requestLimits.weekly.toFixed(2)
                                            )}{' '}
                                            CAD
                                        </Typography>
                                    </div>
                                    {isCurrentLevel && (
                                        <>
                                            <div className={styles.progress_wrapper}>
                                                <div
                                                    className={styles.progress}
                                                    style={{
                                                        width: `${activeTabLimits?.requestLimits.weekly_used / (activeTabLimits?.requestLimits.weekly / 100)}%`
                                                    }}
                                                />
                                            </div>
                                            <div className={styles.row}>
                                                <Typography variant="body4" weight="semibold">
                                                    {prettifyAmount(
                                                        activeTabLimits?.requestLimits.weekly_used.toFixed(
                                                            2
                                                        )
                                                    )}{' '}
                                                    CAD <span>used</span>
                                                </Typography>
                                                <Typography variant="body4" weight="semibold">
                                                    {prettifyAmount(
                                                        (
                                                            (activeTabLimits?.requestLimits
                                                                .weekly || 0) -
                                                            (activeTabLimits?.requestLimits
                                                                .weekly_used || 0)
                                                        ).toFixed(2)
                                                    )}{' '}
                                                    CAD <span>available</span>
                                                </Typography>
                                            </div>
                                        </>
                                    )}
                                </div>
                            )}
                            {activeTabLimits?.requestLimits.monthly > 0 && (
                                <div className={styles.limit_item}>
                                    <div className={styles.row}>
                                        <Typography variant="body3">Monthly requests</Typography>
                                        <Typography variant="body3" weight="semibold">
                                            {prettifyAmount(
                                                activeTabLimits?.requestLimits.monthly.toFixed(2)
                                            )}{' '}
                                            CAD
                                        </Typography>
                                    </div>
                                    {isCurrentLevel && (
                                        <>
                                            <div className={styles.progress_wrapper}>
                                                <div
                                                    className={styles.progress}
                                                    style={{
                                                        width: `${activeTabLimits?.requestLimits.monthly_used / (activeTabLimits?.requestLimits.monthly / 100)}%`
                                                    }}
                                                />
                                            </div>
                                            <div className={styles.row}>
                                                <Typography variant="body4" weight="semibold">
                                                    {prettifyAmount(
                                                        activeTabLimits?.requestLimits.monthly_used.toFixed(
                                                            2
                                                        )
                                                    )}{' '}
                                                    CAD <span>used</span>
                                                </Typography>
                                                <Typography variant="body4" weight="semibold">
                                                    {prettifyAmount(
                                                        (
                                                            (activeTabLimits?.requestLimits
                                                                .monthly || 0) -
                                                            (activeTabLimits?.requestLimits
                                                                .monthly_used || 0)
                                                        ).toFixed(2)
                                                    )}{' '}
                                                    CAD <span>available</span>
                                                </Typography>
                                            </div>
                                        </>
                                    )}
                                </div>
                            )}
                            <div className={styles.transaction_styles}>
                                {activeTabLimits?.requestLimits.per_transfer > 0 && (
                                    <div className={styles.row}>
                                        <Typography variant="body3">
                                            Maximum amount per request
                                        </Typography>
                                        <Typography variant="body3" weight="semibold">
                                            {prettifyAmount(
                                                activeTabLimits?.requestLimits.per_transfer.toFixed(
                                                    2
                                                )
                                            )}{' '}
                                            CAD
                                        </Typography>
                                    </div>
                                )}
                                {activeTabLimits?.requestLimits.amount_transfer_min > 0 && (
                                    <div className={styles.row}>
                                        <Typography variant="body3">
                                            Minimum amount per request
                                        </Typography>
                                        <Typography variant="body3" weight="semibold">
                                            {prettifyAmount(
                                                activeTabLimits?.requestLimits.amount_transfer_min.toFixed(
                                                    2
                                                )
                                            )}{' '}
                                            CAD
                                        </Typography>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </motion.div>
    );
};

export default SettingsLimitsLayout;
